<script lang="ts">
import PolicyDocumentUploadListModal from '@/views/account/client_readiness/PolicyDocumentUploadListModal.vue';
import { ExternalUser } from '@/types/User';
import { AccountServicingRole } from '@/types/AccountSummary';

export default {
  name: 'AccountClientReadiness',
  components: {
    PolicyDocumentUploadListModal,
  },
};

interface ExternalUserDisplay extends ExternalUser {
  relativeLastActive: string;
}

// tooltips
const warningToolTip = 'Needs Attention';
const policiesWithNoBinderTooltip =
  'Policies without an uploaded binder document. To upload a binder, attach the file in Epic.';
const policiesWithNoPolicyDocumentTooltip =
  'Policies without an uploaded copy of the policy document. To upload a policy, attach the file in Epic or visit the Documents tab.';
const policiesWithUnpublishedBinderTooltip =
  'Policies that have an uploaded binder that is not published for a client to view. Visit the Documents tab to publish these binders.';
const policiesWithUnpublishedPolicyDocumentTooltip =
  'Policies that have an uploaded policy document that is not published for a client to view. Visit the Documents tab to publish these policies.';
const policiesWithPossibleInvalidPolicyNumberTooltip =
  'Policies with a possibly invalid policy number, such as "TBD", blank, or more than 25 characters. These policy numbers must be changed in Epic. The new policy number will appear on the Platform after the data refreshes overnight.';
const policiesWithNoPremiumOrFeeTooltip =
  'Policies without associated premiums for fees. Premium and fee information can be updated in Epic, and changes will be reflected on the Platform after the data refreshes overnight.';
const policiesWithInvalidDatesTooltip =
  'Policies with suspicious effective or expiration dates, such as single-day coverage.'
</script>

<script setup lang="ts">
import { accountService } from '@/services/api';
import { computed, onBeforeMount, ref, watch, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { relativeDateTime } from '@/utilities/Dates';
import { Account } from '@/types/Account';
import { CheckCircle2, AlertTriangle, PencilIcon as Pencil, FileUp, Info } from 'lucide-vue-next';
import { OwnershipType } from '@/types/StockExchange';
import clientReadinessService, {
  DocumentsKPI,
  PoliciesKPI,
  RuleViolatingPolicy,
  RuleViolatingProgram,
} from '@/services/v2/ClientReadinessService';
import { addCommas } from '@/utilities/Number';
import EditPolicyModal from '@/components/EditPolicyModal.vue';
import PoliciesCollapseView from '@/components/v2/client_readiness/PoliciesCollapseView.vue';
import ProgramsCollapseView from '@/components/v2/client_readiness/ProgramsCollapseView.vue';

const props = defineProps<{
  account: Account;
}>();

const router = useRouter();

// -----
// refs
// -----

const showNonCAC = ref(false);
const showCurrentOnly = ref(true);

const editPolicy = reactive({
  show: false,
  policyId: 0,
  mainPolicyNumber: '',
});

const externalUsers = ref<ExternalUserDisplay[]>([]);
const accountServicers = ref<AccountServicingRole[]>([]);

const documentsKPI = ref<DocumentsKPI>({
  policiesWithNoBinderCount: 0,
  policiesWithNoPolicyDocumentCount: 0,
  unpublishedBinderCount: 0,
  unpublishedPolicyDocumentCount: 0,
});

const policiesKPI = ref<PoliciesKPI>({
  policiesWithNoPremiumOrFeesCount: 0,
  policiesWithInvalidDatesCount: 0,
  policiesWithPossibleInvalidPolicyNumberCount: 0,
  totalPolicyCount: 0,
});

const documentsKPICache = ref<Map<string, DocumentsKPI>>(new Map<string, DocumentsKPI>());
const policiesKPICache = ref<Map<string, PoliciesKPI>>(new Map<string, PoliciesKPI>());

const showPolicyDocumentUploadListModal = ref(false);
const showBinderUploadListModal = ref(false);

const ruleViolatingPolicies = ref<RuleViolatingPolicy[]>([]);
const ruleViolatingPrograms = ref<RuleViolatingProgram[]>([]);

const ruleViolatingPoliciesCache = ref<Map<string, RuleViolatingPolicy[]>>(new Map<string, RuleViolatingPolicy[]>());
const ruleViolatingProgramsCache = ref<Map<string, RuleViolatingProgram[]>>(new Map<string, RuleViolatingProgram[]>());

// ---------
// computed
// ---------

const accountId = computed(() => useRoute().params.id as string);
const ownershipType = computed(() => props.account.ownershipType ?? '--');
const cacIndustryName = computed(() => props.account.industryName ?? '--');
const sicIndustryName = computed(() => props.account.sicDescription ?? '--');

const stockTicker = computed(() => {
  if (!props.account.stockTicker || !props.account.stockExchange) {
    return '--';
  }
  return props.account.stockTicker;
});

const stockExchange = computed(() => {
  return props.account.stockExchange;
});

const website = computed(() => {
  if (props.account.noDomain) {
    return 'Website does not exist';
  }
  return props.account.domainName ?? '--';
});

const missingOwnershipType = computed(() => !props.account.ownershipType);
const missingCacIndustryName = computed(() => !props.account.industryName);
const missingSicIndustryName = computed(() => !props.account.sicDescription);
const missingStockTicker = computed(() => !props.account.stockTicker || !props.account.stockExchange);
const missingWebSite = computed(() => {
  // if true, then user explicitly set no website, so it's not missing
  if (props.account.noDomain) {
    return false;
  }
  return !props.account.domainName;
});

const hasPoliciesWithoutPolicyDocument = computed(() => documentsKPI.value.policiesWithNoPolicyDocumentCount > 0);
const hasPoliciesWithoutBinder = computed(() => documentsKPI.value.policiesWithNoBinderCount > 0);


const policiesWithoutLimit = computed(() => ruleViolatingPolicies.value.filter((policy) => policy.noLimitRule));
const policiesWithoutRetention = computed(() =>
  ruleViolatingPolicies.value.filter((policy) => policy.invalidRetentionRule),
);
const policiesWithoutAttachmentPoint = computed(() =>
  ruleViolatingPolicies.value.filter((policy) => policy.invalidAttachmentPointRule),
);
const programsWithNoServicingRoles = computed(() =>
  ruleViolatingPrograms.value.filter((program) => program.noServicingRolesRule),
);

const servicersByRole = computed(() => {
  const grouped = new Map<string, AccountServicingRole[]>();
  
  accountServicers.value.forEach(servicer => {
    if (!grouped.has(servicer.servicingRoleTitle)) {
      grouped.set(servicer.servicingRoleTitle, []);
    }
    grouped.get(servicer.servicingRoleTitle)?.push(servicer);
  });
  
  return grouped;
});

// -----------
// lifecycles
// -----------

onBeforeMount(async () => {
  await Promise.all([fetchExternalUsers(), fetchAccountServicers(), fetchKPIs(), fetchRuleViolatingPolicies(), fetchRuleViolatingPrograms()]);
});

// ---------
// watchers
// ---------

watch([showNonCAC, showCurrentOnly], async () => {
  await Promise.all([fetchDocumentsKPI(), fetchPoliciesKPI(), fetchRuleViolatingPolicies(), fetchRuleViolatingPrograms()]);
});

// ----------
// functions
// ----------

async function fetchExternalUsers(): Promise<void> {
  externalUsers.value = (await accountService.getExternalUsersForAccount(accountId.value)).map((user) => {
    if (!user.lastActive) return { ...user, lastActive: undefined } as ExternalUserDisplay;

    return {
      ...user,
      lastActive: new Date(user.lastActive).toLocaleDateString(undefined, { dateStyle: 'short' }),
      relativeLastActive: relativeDateTime(user.lastActive),
    } as ExternalUserDisplay;
  });
}

async function fetchAccountServicers(): Promise<void> {
  accountServicers.value = await accountService.getAccountServicers(accountId.value);
}

async function fetchKPIs(): Promise<void> {
  await Promise.all([fetchDocumentsKPI(), fetchPoliciesKPI()]);
}

async function fetchDocumentsKPI(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (documentsKPICache.value.has(JSON.stringify(queryParams))) {
    documentsKPI.value = documentsKPICache.value.get(JSON.stringify(queryParams)) as DocumentsKPI;
  } else {
    documentsKPI.value = await clientReadinessService.getDocumentsKPI(Number(accountId.value), queryParams);
    documentsKPICache.value.set(JSON.stringify(queryParams), documentsKPI.value);
  }
}

async function fetchPoliciesKPI(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (policiesKPICache.value.has(JSON.stringify(queryParams))) {
    policiesKPI.value = policiesKPICache.value.get(JSON.stringify(queryParams)) as PoliciesKPI;
  } else {
    policiesKPI.value = await clientReadinessService.getPoliciesKPI(Number(accountId.value), queryParams);
    policiesKPICache.value.set(JSON.stringify(queryParams), policiesKPI.value);
  }
}

async function fetchRuleViolatingPolicies(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (ruleViolatingPoliciesCache.value.has(JSON.stringify(queryParams))) {
    ruleViolatingPolicies.value = ruleViolatingPoliciesCache.value.get(
      JSON.stringify(queryParams),
    ) as RuleViolatingPolicy[];
  } else {
    ruleViolatingPolicies.value = await clientReadinessService.getRuleViolatingPolicies(
      Number(accountId.value),
      queryParams,
    );
    ruleViolatingPoliciesCache.value.set(JSON.stringify(queryParams), ruleViolatingPolicies.value);
  }
}

async function fetchRuleViolatingPrograms(): Promise<void> {
  const queryParams = {
    showNonCAC: showNonCAC.value,
    showCurrentOnly: showCurrentOnly.value,
  };

  if (ruleViolatingProgramsCache.value.has(JSON.stringify(queryParams))) {
    ruleViolatingPrograms.value = ruleViolatingProgramsCache.value.get(
      JSON.stringify(queryParams),
    ) as RuleViolatingProgram[];
  } else {
    ruleViolatingPrograms.value = await clientReadinessService.getRuleViolatingPrograms(
      Number(accountId.value),
      queryParams,
    );
    ruleViolatingProgramsCache.value.set(JSON.stringify(queryParams), ruleViolatingPrograms.value);
  }
}

function handleEditAccount() {
  router.push(`/account/${accountId.value}/edit`);
}

async function handleDocumentUploaded() {
  documentsKPICache.value.clear();
  await fetchDocumentsKPI();
}

function handleEditPolicy(policyId: number, mainPolicyNumber: string) {
  editPolicy.policyId = policyId;
  editPolicy.mainPolicyNumber = mainPolicyNumber;
  editPolicy.show = true;
}

function handlePolicySave() {
  editPolicy.show = false;
  ruleViolatingPoliciesCache.value.clear();
  fetchRuleViolatingPolicies();
}

async function handleToggleServicerPublished(servicer: AccountServicingRole) {
  try {
    const newPublishedState = !servicer.isPublishedOnSomePolicies;
    await accountService.toggleServicerPublished(
      accountId.value,
      servicer.name,
      servicer.servicingRoleTitle,
      newPublishedState,
    );
    // Refetch the servicers to get the updated state
    await fetchAccountServicers();
  } catch (error) {
    console.error('Failed to toggle servicer published status:', error);
    // Optionally show a toast/notification to the user
  }
}
</script>

<template>
  <div class="tw:flex tw:flex-col tw:items-center tw:px-2">

    <!-- KPI panel -->
    <div
      class="tw:mt-4 tw:w-full tw:max-w-7xl tw:flex-1 tw:rounded-lg tw:border tw:border-solid tw:border-base-300 tw:p-4 tw:pt-0 tw:shadow-sm"
    >
      <div class="tw:flex tw:w-full tw:justify-end tw:py-1 tw:gap-2">
        <label class="tw:label tw:cursor-pointer tw:gap-2">
          <span class="tw:label-text tw:text-xs tw:transition-all">Show Current Only</span>
          <input
            type="checkbox"
            v-model="showCurrentOnly"
            class="tw:toggle tw:toggle-primary tw:toggle-sm tw:border-solid"
          />
        </label>
        <label class="tw:label tw:cursor-pointer tw:gap-2">
          <span class="tw:label-text tw:text-xs tw:transition-all">Show Non-CAC</span>
          <input
            type="checkbox"
            v-model="showNonCAC"
            class="tw:toggle tw:toggle-primary tw:toggle-sm tw:border-solid"
          />
        </label>
      </div>
      
      <!-- POLICIES KPI -->
      <div class="tw:grid tw:w-full tw:max-w-7xl tw:flex-1 tw:grid-cols-2 tw:gap-4 tw:lg:grid-cols-7">
        <div class="tw:col-span-7 tw:space-y-4">
          <div class="tw:card tw:card-border tw:relative tw:rounded-lg">
            <div class="tw:absolute tw:-top-2 tw:flex tw:w-full tw:justify-center">
              <h4 class="tw:bg-white tw:px-2 tw:text-center tw:text-xs tw:text-gray-500 tw:uppercase">
                Policies ({{ policiesKPI.totalPolicyCount }} Total)
              </h4>
            </div>
            <div
              class="tw:stats tw:stats-vertical tw:grid-cols-none tw:overflow-visible tw:lg:grid-cols-7 tw:gap-1"
            >
              <div class="tw:stat tw:rounded-t-lg tw:shadow-sm tw:lg:rounded-bl-lg tw:lg:rounded-br-none tw:lg:rounded-tl-lg tw:lg:rounded-tr-none">
                <div v-if="hasPoliciesWithoutBinder"
                     class="tw:stat-figure tw:text-gray-500 tw:hover:text-secondary">
                  <div class="tw:tooltip" data-tip="Attach Binders">
                    <button @click="showBinderUploadListModal = true">
                      <FileUp />
                    </button>
                  </div>
                </div>
                <div class="tw:peer tw:stat-value tw:w-fit">
                  {{ addCommas(documentsKPI.policiesWithNoBinderCount) }}
                </div>
                <div class="tw:stat-desc tw:text-gray-800">No Binder Attached</div>
                <div
                  class="tw:tooltip tw:peer-hover:tooltip-open tw:lg:tooltip-right tw:lg:-left-20 tw:lg:-top-10"
                  :data-tip="policiesWithNoBinderTooltip"
                ></div>
              </div>
              <div class="tw:stat tw:shadow-sm">
                <div v-if="hasPoliciesWithoutPolicyDocument" class="tw:stat-figure tw:text-gray-500 tw:hover:text-secondary">
                  <div class="tw:tooltip" data-tip="Attach Policy Documents">
                    <button @click="showPolicyDocumentUploadListModal = true">
                      <FileUp />
                    </button>
                  </div>
                </div>
                <div class="tw:peer tw:stat-value tw:w-fit">
                  {{ addCommas(documentsKPI.policiesWithNoPolicyDocumentCount) }}
                </div>
                <div class="tw:stat-desc tw:text-gray-800">No Policy Attached</div>
                <div
                  class="tw:tooltip tw:peer-hover:tooltip-open tw:lg:tooltip-right tw:lg:-left-20 tw:lg:-top-10"
                  :data-tip="policiesWithNoPolicyDocumentTooltip"
                ></div>
              </div>
              <div class="tw:stat tw:shadow-sm">
                <div class="tw:peer tw:stat-value tw:w-fit">
                  {{ addCommas(documentsKPI.unpublishedBinderCount) }}
                </div>
                <div class="tw:stat-desc tw:text-gray-800">No Binder Published</div>
                <div
                  class="tw:tooltip tw:peer-hover:tooltip-open tw:lg:tooltip-right tw:lg:-left-20 tw:lg:-top-10"
                  :data-tip="policiesWithUnpublishedBinderTooltip"
                ></div>
              </div>
              <div class="tw:stat tw:shadow-sm">
                <div class="tw:peer tw:stat-value tw:w-fit">
                  {{ addCommas(documentsKPI.unpublishedPolicyDocumentCount) }}
                </div>
                <div class="tw:stat-desc tw:text-gray-800">No Policy Published</div>
                <div
                  class="tw:tooltip tw:peer-hover:tooltip-open tw:lg:tooltip-right tw:lg:-left-20 tw:lg:-top-10"
                  :data-tip="policiesWithUnpublishedPolicyDocumentTooltip"
                ></div>
              </div>
              <div class="tw:stat tw:shadow-sm">
                <div class="tw:peer tw:stat-value tw:w-fit">
                  {{ addCommas(policiesKPI.policiesWithPossibleInvalidPolicyNumberCount) }}
                </div>
                <div class="tw:stat-desc tw:text-gray-800">Invalid Policy Number</div>
                <div
                  class="tw:tooltip tw:peer-hover:tooltip-open tw:lg:tooltip-right tw:lg:-left-20 tw:lg:-top-10"
                  :data-tip="policiesWithPossibleInvalidPolicyNumberTooltip"
                ></div>
              </div>
              <div class="tw:stat tw:shadow-sm">
                <div class="tw:peer tw:stat-value tw:w-fit">
                  {{ addCommas(policiesKPI.policiesWithNoPremiumOrFeesCount) }}
                </div>
                <div class="tw:stat-desc tw:text-gray-800">No Premium or Fee</div>
                <div class="tw:tooltip tw:peer-hover:tooltip-open tw:lg:-left-20 tw:lg:-top-10"
                     :data-tip="policiesWithNoPremiumOrFeeTooltip"
                />
              </div>
              <div class="tw:stat tw:rounded-b-lg tw:shadow-sm tw:lg:rounded-bl-none tw:lg:rounded-br-lg tw:lg:rounded-tl-none tw:lg:rounded-tr-lg">
                <div class="tw:peer tw:stat-value tw:w-fit">
                  {{ addCommas(policiesKPI.policiesWithInvalidDatesCount) }}
                </div>
                <div class="tw:stat-desc tw:text-gray-800">Invalid Dates</div>
                <div class="tw:tooltip tw:peer-hover:tooltip-open tw:lg:-left-20 tw:lg:-top-10" :data-tip="policiesWithInvalidDatesTooltip" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="tw:grid tw:w-full tw:max-w-7xl tw:flex-1 tw:grid-cols-2 tw:gap-4 tw:pt-8 tw:md:grid-cols-9">

      <!-- Account Details -->
      <div class="tw:col-span-3 tw:flex tw:flex-col tw:gap-4">
        <div class="tw:card tw:card-border tw:rounded-lg tw:shadow-sm">
          <div class="tw:card-body tw:p-4">
            <div class="tw:pb-2">
              <h2 class="tw:card-title tw:justify-between">
                <div>Account Details</div>
                <button
                  @click="handleEditAccount"
                  class="tw:btn tw:btn-sm tw:hover:bg-secondary tw:hover:text-base-200"
                >
                  <Pencil :size="13" />
                </button>
              </h2>
            </div>
            <div class="tw:flex tw:flex-col tw:gap-4">
              <div class="tw:flex tw:items-center tw:justify-between tw:rounded-sm tw:p-4 tw:shadow-sm">
                <div>
                  <div class="tw:text-base tw:text-gray-500">CAC Industry</div>
                  <div class="tw:text-lg" :class="{ 'tw:text-red-500': missingCacIndustryName }">
                    {{ cacIndustryName }}
                  </div>
                </div>
                <CheckCircle2 v-if="!missingCacIndustryName" class="tw:text-green-500" />
                <div v-else class="tw:tooltip" :data-tip="warningToolTip">
                  <AlertTriangle class="tw:text-red-500" />
                </div>
              </div>
              <div class="tw:flex tw:items-center tw:justify-between tw:rounded-sm tw:p-4 tw:shadow-sm">
                <div>
                  <div class="tw:text-base tw:text-gray-500">SIC Industry</div>
                  <div class="tw:text-lg" :class="{ 'tw:text-red-500': missingSicIndustryName }">
                    {{ sicIndustryName }}
                  </div>
                </div>
                <CheckCircle2 v-if="!missingSicIndustryName" class="tw:text-green-500" />
                <div v-else class="tw:tooltip" :data-tip="warningToolTip">
                  <AlertTriangle class="tw:text-red-500" />
                </div>
              </div>
              <div class="tw:flex tw:items-center tw:justify-between tw:rounded-sm tw:p-4 tw:shadow-sm">
                <div>
                  <div class="tw:text-base tw:text-gray-500">Ownership Type</div>
                  <div class="tw:text-lg" :class="{ 'tw:text-red-500': missingOwnershipType }">{{ ownershipType }}</div>
                </div>
                <CheckCircle2 v-if="!missingOwnershipType" class="tw:text-green-500" />
                <div v-else class="tw:tooltip" :data-tip="warningToolTip">
                  <AlertTriangle class="tw:text-red-500" />
                </div>
              </div>
              <div
                v-if="!missingOwnershipType && ownershipType === OwnershipType.Public"
                class="tw:flex tw:items-center tw:justify-between tw:rounded-sm tw:p-4 tw:shadow-sm"
              >
                <div>
                  <div class="tw:text-base tw:text-gray-500">Stock Ticker</div>
                  <div class="tw:text-lg" :class="{ 'tw:text-red-500': missingStockTicker }">{{ stockTicker }}</div>
                  <div v-if="!missingStockTicker" class="tw:text-xs tw:text-gray-500">{{ stockExchange }}</div>
                </div>
                <CheckCircle2 v-if="!missingStockTicker" class="tw:text-green-500" />
                <div v-else class="tw:tooltip" :data-tip="warningToolTip">
                  <AlertTriangle class="tw:text-red-500" />
                </div>
              </div>
              <div class="tw:flex tw:items-center tw:justify-between tw:rounded-sm tw:p-4 tw:shadow-sm">
                <div>
                  <div class="tw:text-base tw:text-gray-500">Website</div>
                  <div class="tw:text-lg" :class="{ 'tw:text-red-500': missingWebSite }">{{ website }}</div>
                </div>
                <CheckCircle2 v-if="!missingWebSite" class="tw:text-green-500" />
                <div v-else class="tw:tooltip" :data-tip="warningToolTip">
                  <AlertTriangle class="tw:text-red-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="tw:card tw:card-border tw:rounded-lg tw:border-solid tw:shadow-sm">
          <div class="tw:card-body tw:p-4">
            <h2 class="tw:card-title tw:pb-2">External Users</h2>
            <table v-if="externalUsers.length > 0" class="apptable dense --tight-first">
              <thead>
                <tr>
                  <td>User</td>
                  <td>Email</td>
                  <td>Last Login</td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in externalUsers" :key="user.id">
                  <td>{{ user.name }}</td>
                  <td class="tw:break-all">{{ user.email }}</td>
                  <td>
                    <div>{{ user.lastActive }}</div>
                    <div class="tw:text-xs tw:text-gray-600">{{ user.relativeLastActive }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="tw:flex tw:justify-center">
              <div class="tw:alert tw:flex tw:w-96 tw:justify-center tw:bg-gray-100">
                No external users have been added to this account.
              </div>
            </div>
          </div>
        </div>
        <div class="tw:card tw:card-border tw:rounded-lg tw:border-solid tw:shadow-sm">
          <div class="tw:card-body tw:p-4">
            <h2 class="tw:card-title tw:pb-2">Account Service Personnel</h2>
            <div v-if="accountServicers.length > 0" class="tw:flex tw:flex-col tw:gap-4">
              <div v-for="[roleTitle, servicers] in servicersByRole" :key="roleTitle" class="tw:rounded-sm tw:shadow-sm">
                <div class="tw:bg-gray-50 tw:p-2 tw:text-sm tw:font-semibold tw:text-gray-700">
                  {{ roleTitle }}
                </div>
                <div class="tw:divide-y">
                  <div 
                    v-for="servicer in servicers" 
                    :key="`${servicer.name}-${servicer.servicingRoleTitle}`"
                    class="tw:flex tw:items-center tw:justify-between tw:p-3"
                  >
                    <div class="tw:text-base">{{ servicer.name }}</div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <div 
                        v-if="servicer.isPublishedOnSomePolicies && servicer.isUnpublishedOnSomePolicies"
                        class="tw:tooltip tw:tooltip-left"
                        data-tip="Servicer is hidden on some policies"
                      >
                        <Info :size="16" class="tw:text-gray-500" />
                      </div>
                  
                       <label class="tw:label tw:cursor-pointer tw:gap-2">
                         <span class="tw:label-text tw:text-xs">Client Visible</span>
                         <input
                           type="checkbox"
                           :checked="servicer.isPublishedOnSomePolicies"
                           @change="handleToggleServicerPublished(servicer)"
                           class="tw:toggle tw:toggle-primary tw:toggle-sm tw:border-solid"
                         />
                       </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="tw:flex tw:justify-center">
              <div class="tw:alert tw:flex tw:w-96 tw:justify-center tw:bg-gray-100">
                No servicers have been assigned to this account.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rule Violation -->
      <div
        class="tw:card tw:card-border tw:col-span-6 tw:row-span-2 tw:self-start tw:rounded-lg tw:shadow-sm"
      >
        <div class="tw:card-body tw:p-4">
          <h2 class="tw:card-title tw:pb-2">Rule Violations</h2>
          <PoliciesCollapseView
            title="Policies Without a Limit"
            :policies="policiesWithoutLimit"
            @editPolicy="handleEditPolicy"
          />
          <PoliciesCollapseView
            title="Primary Policies Without a Retention"
            :policies="policiesWithoutRetention"
            @editPolicy="handleEditPolicy"
          />
          <PoliciesCollapseView
            title="Excess Policies Without an Attachment Point"
            :policies="policiesWithoutAttachmentPoint"
            @editPolicy="handleEditPolicy"
          />
          <ProgramsCollapseView
            title="Programs Without Servicing Roles"
            :programs="programsWithNoServicingRoles"
            :actions="[]"
          />
        </div>
      </div>
    </div>
    
    <PolicyDocumentUploadListModal
      :accountId="Number(accountId)"
      :ShowNonCAC="showNonCAC"
      :ShowCurrentOnly="showCurrentOnly"
      doc-type="Policy"
      title="Upload Policy Documents"
      v-if="showPolicyDocumentUploadListModal"
      @close="showPolicyDocumentUploadListModal = false"
      @document-uploaded="handleDocumentUploaded"
    />
    
    <PolicyDocumentUploadListModal
      :accountId="Number(accountId)"
      :ShowNonCAC="showNonCAC"
      :ShowCurrentOnly="showCurrentOnly"
      doc-type="Binder"
      title="Upload Binders"
      v-if="showBinderUploadListModal"
      @close="showBinderUploadListModal = false"
      @document-uploaded="handleDocumentUploaded"
    />
    
    <EditPolicyModal
      :show="editPolicy.show"
      :policy-id="editPolicy.policyId"
      :policy-number="editPolicy.mainPolicyNumber"
      @close="console.log('test'); editPolicy.show = false;"
      @save="handlePolicySave"
    />
  </div>
</template>
